import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fullName } from '../utils/format.js';

/**
 * Pick colleagues by typing, not by scrolling.
 *
 * A list of everyone is fine with eight accounts and useless with a hundred, so
 * nothing is shown until you type: the field filters on name, e-mail and role,
 * and the chosen people stay visible as removable tags above it.
 *
 * `multiple` turns it into the group builder; on its own it picks one person.
 */
const PeoplePicker = ({ people, value = [], onChange, multiple = false, placeholder, autoFocus = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const chosen = useMemo(
    () => value.map((id) => people.find((person) => person.id === id)).filter(Boolean),
    [value, people],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return people
      .filter((person) => !value.includes(person.id))
      .filter((person) =>
        [fullName(person), person.email, t(`roles.${person.role}`)]
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 8);
  }, [query, people, value, t]);

  const add = (person) => {
    onChange(multiple ? [...value, person.id] : [person.id]);
    setQuery('');
  };

  return (
    <div className="picker">
      {chosen.length ? (
        <div className="picker__tags">
          {chosen.map((person) => (
            <span key={person.id} className="picker__tag">
              {fullName(person)}
              <button
                type="button"
                aria-label={t('common.delete')}
                onClick={() => onChange(value.filter((id) => id !== person.id))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <input
        className="input"
        value={query}
        autoFocus={autoFocus}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={placeholder ?? t('chat.searchPerson')}
        aria-label={placeholder ?? t('chat.searchPerson')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && matches.length) {
            event.preventDefault();
            add(matches[0]);
          }
        }}
      />

      {query.trim() && !matches.length ? <p className="picker__none muted">{t('chat.noMatch')}</p> : null}

      {matches.length ? (
        <ul className="picker__list">
          {matches.map((person) => (
            <li key={person.id}>
              <button type="button" className="picker__option" onClick={() => add(person)}>
                <span>{fullName(person)}</span>
                <span className="muted">{t(`roles.${person.role}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default PeoplePicker;
